import { DateTime } from "luxon";

/**
 * Format a timestamp for display in the inbox conversation list
 * - Same day: "2:30 PM"
 * - Yesterday: "Yesterday"
 * - Same week: "Monday"
 * - Same year: "Jan 15"
 * - Different year: "Jan 15, 2023"
 */
export const formatTimestamp = (dateStr: string | null): string => {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  const now = new Date();

  // Helper to check if same day
  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  // Helper to check if same week
  const isSameWeek = (d1: Date, d2: Date) => {
    const startOfWeek = new Date(d2);
    startOfWeek.setDate(d2.getDate() - d2.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return d1 >= startOfWeek && d1 <= endOfWeek;
  };

  // Same day - show time (e.g., "2:30 PM")
  if (isSameDay(date, now))
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) return "Yesterday";

  // Same week - show day name (e.g., "Monday")
  if (isSameWeek(date, now))
    return date.toLocaleDateString("en-US", { weekday: "long" });

  // Same year - show month and day (e.g., "Jan 15")
  if (date.getFullYear() === now.getFullYear())
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  // Different year - show full date (e.g., "Jan 15, 2023")
  return DateTime.fromISO(dateStr).toFormat("MMM d, yyyy");
};
